import { ChevronDown, Download, Loader2 } from "lucide-react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { ExportFormat } from "../../../api/download";

export interface DownloadFormatOption {
  format: ExportFormat;
  label: string;
}

interface DownloadFormatMenuProps {
  onSelect: (format: ExportFormat) => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: "button" | "icon";
  options?: DownloadFormatOption[];
}

const DEFAULT_OPTIONS: DownloadFormatOption[] = [
  { format: ExportFormat.PDF, label: "Download as PDF" },
  { format: ExportFormat.DOCX, label: "Download as Docx" },
];

const DownloadFormatMenu = ({
  onSelect,
  disabled,
  loading = false,
  variant = "button",
  options = DEFAULT_OPTIONS,
}: DownloadFormatMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const isDisabled = disabled || loading;

  const triggerIcon = loading ? (
    <Loader2 size={16} strokeWidth={2} className="animate-spin" />
  ) : (
    <Download size={16} strokeWidth={2} />
  );

  return (
    <div ref={ref} className="relative">
      {variant === "icon" ? (
        <button
          type="button"
          aria-label="Download"
          disabled={isDisabled}
          onClick={toggle}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E5E5] bg-white text-[#525252] shadow-sm transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {triggerIcon}
        </button>
      ) : (
        <button
          type="button"
          disabled={isDisabled}
          onClick={toggle}
          className="flex h-9 items-center justify-center gap-2 rounded-md border border-[#3551F3] bg-white px-4 py-2 text-sm font-medium text-[#3551F3] whitespace-nowrap transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {triggerIcon}
          Download
          <ChevronDown size={16} strokeWidth={2} />
        </button>
      )}

      {open && (
        <div className="absolute right-0 z-10 mt-1 min-w-[180px] overflow-hidden rounded-md border border-[#E5E5E5] bg-white py-1 shadow-md">
          {options.map((option) => (
            <button
              key={option.format}
              type="button"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setOpen(false);
                onSelect(option.format);
              }}
              className="flex w-full items-center px-3 py-2 text-left text-sm text-[#171717] hover:bg-[#F5F5F4]"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadFormatMenu;
